<script>
import { toMoney, getNumber, abreviarValor } from '@utils/Format'
import {
  calculateStackedYAxisLimits,
  getChartOptions,
} from '@modules/comissoes/utils/chartUtils'
import {
  getCurrencyByInvestmentType,
  getMoneySymbolByCurrency,
} from '@modules/consolidator/utils/InvestmentType/InvestmentType.js'
import ChartjsLegend from '@modules/global/components/charts/chartjs-legend.vue'
import NoDataMessage from '@modules/consolidator/components/no-data-message/no-data-message.vue'

export default {
  name: 'PatrimonioChart',
  components: {
    ChartjsLegend,
    NoDataMessage,
  },
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    investmentType: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      hiddenOptions: {},
    }
  },
  computed: {
    hasData() {
      return (
        this.data &&
        Array.isArray(this.data.labels) &&
        this.data.labels.length > 0 &&
        Array.isArray(this.data.datasets) &&
        this.data.datasets.some(
          (dataset) => Array.isArray(dataset.data) && dataset.data.length > 0
        )
      )
    },
    formattedChartData() {
      if (!this.hasData) {
        return {
          labels: [],
          datasets: (this.data && this.data.datasets) || [],
        }
      }

      const formattedData = JSON.parse(JSON.stringify(this.data))

      formattedData.datasets = formattedData.datasets.map((item) => {
        return {
          ...item,
          hidden: this.hiddenOptions[item.label] || false,
        }
      })

      return formattedData
    },
    chartOptions() {
      const axisFormatter = (value) => {
        const numericValue = getNumber(value)
        return `${this.moneySymbol} ${abreviarValor(numericValue)}`
      }

      return getChartOptions(
        this.chartBarOptions,
        this.yAxisLimits,
        axisFormatter
      )
    },
    chartBarOptions() {
      const currencyKey = this.moneyCurrency

      return {
        scales: {
          yAxes: [
            {
              id: 'first-y-axis',
              position: 'left',
              ticks: {
                display: true,
                beginAtZero: true,
                fontSize: 10,
              },
              gridLines: {
                display: true,
              },
            },
          ],
          xAxes: [
            {
              ticks: {
                beginAtZero: true,
              },
              gridLines: {
                display: false,
              },
            },
          ],
        },
        legend: {
          display: false,
        },
        tooltips: {
          enabled: true,
          mode: 'index',
          callbacks: {
            label: (tooltipItems) => {
              const numericValue = getNumber(tooltipItems.yLabel)
              return toMoney(numericValue, 2, currencyKey)
            },
          },
        },
        responsive: true,
        maintainAspectRatio: false,
      }
    },
    yAxisLimits() {
      const acceptValuesNegative = true
      return calculateStackedYAxisLimits(
        this.data.datasets,
        acceptValuesNegative
      )
    },
    moneyCurrency() {
      return getCurrencyByInvestmentType({
        investmentType: this.investmentType,
      })
    },
    moneySymbol() {
      return getMoneySymbolByCurrency({
        currencyKey: this.moneyCurrency,
      })
    },
  },
  methods: {
    toggleDatasetVisibility(label) {
      this.$set(this.hiddenOptions, label, !this.hiddenOptions[label])
    },
  },
}
</script>

<template>
  <div class="patrimonio-chart">
    <div v-if="hasData" class="patrimonio-chart__content">
      <bar-chart :chart-data="formattedChartData" :options="chartOptions" />
      <ChartjsLegend
        :datasets="formattedChartData.datasets"
        :on-toggle="toggleDatasetVisibility"
        class="patrimonio-chart__legend"
      />
    </div>
    <NoDataMessage v-else class="patrimonio-chart__no-data" />
  </div>
</template>

<style lang="sass">
@import '@modules/consolidator/design/componentes/chartjs-legend.sass'

.patrimonio-chart
  height: 100%
  min-height: 450px
  display: flex
  flex-direction: column

  &__content
    flex: 1
    display: flex
    flex-direction: column

  &__no-data
    flex: 1

  &__legend
    @extend .chartjs-legend
</style>
